import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";

import { sync } from "@/core/sync";
import { BINGO_EVENTS, makeEvent } from "@/core/sync/events";

/**
 * bingoSettingsStore — plugin-scoped Bingo Show settings.
 *
 *   • specialEffectsEnabled  — 9 unique per-special-number animations
 *   • nicknamesEnabled       — UK bingo nicknames under specials
 *   • revealTiming           — six configurable durations that control
 *                              the standard-ball reveal sequence so the
 *                              host can dial in the show's pacing live.
 *
 * Persists to localStorage AND broadcasts BINGO_EVENTS.SETTINGS_UPDATED
 * across BroadcastChannel so an open Projector window updates instantly.
 */

/**
 * Timing defaults (milliseconds).
 * The reveal sequence is:
 *   [ballAppear] → [ballSpin] → [ballOpen] → [revealPause] → [magicTrail] → cell lit
 *      A            B            C            D               E              F
 * ...and the Board's landing pulse (F) lasts `boardPulseMs` on the cell.
 * Total = 300 + 900 + 350 + 400 + 400 = 2,350ms ≈ 2.35s (design brief 2–2.5s).
 */
export const DEFAULT_TIMING = Object.freeze({
  ballAppearMs: 300,
  ballSpinMs: 900,
  ballOpenMs: 350,
  revealPauseMs: 400,
  magicTrailMs: 400,
  boardPulseMs: 700,
});

/**
 * Slider bounds — narrow enough to prevent absurd values that would break
 * the show, wide enough to give real creative range for a live audience.
 */
export const TIMING_BOUNDS = Object.freeze({
  ballAppearMs: { min: 150, max: 800, step: 25 },
  ballSpinMs: { min: 400, max: 2500, step: 50 },
  ballOpenMs: { min: 200, max: 900, step: 25 },
  revealPauseMs: { min: 200, max: 1500, step: 25 },
  magicTrailMs: { min: 200, max: 1200, step: 25 },
  boardPulseMs: { min: 300, max: 1600, step: 25 },
});

/**
 * TIMING_LABELS — human-readable copy for the settings sliders.
 */
export const TIMING_LABELS = Object.freeze({
  ballAppearMs: {
    title: "Ball appears",
    hint: "Time for the ball to fly in and scale up.",
  },
  ballSpinMs: {
    title: "Ball spins",
    hint: "Suspense phase before the ball opens.",
  },
  ballOpenMs: {
    title: "Ball opens",
    hint: "Duration of the top/bottom halves splitting apart.",
  },
  revealPauseMs: {
    title: "Number reveal pause",
    hint: "How long the number sits in the ball before the trail fires.",
  },
  magicTrailMs: {
    title: "Magic trail",
    hint: "Time for the sparkle streak to arc onto the board.",
  },
  boardPulseMs: {
    title: "Board highlight pulse",
    hint: "Length of the landing glow on the target cell.",
  },
});

const defaultBingoSettings = {
  specialEffectsEnabled: true,
  nicknamesEnabled: false,
  revealTiming: { ...DEFAULT_TIMING },
  /**
   * Per Phase-2 module enable flags. Keys match module.key in the
   * phase2Specials registry. Add new keys here as new modules ship.
   */
  specials: {
    doubles: true,
    sixSeven: true,
    shyBall: true,
  },
};

const broadcast = (patch) => {
  sync.publish(
    makeEvent(BINGO_EVENTS.SETTINGS_UPDATED, patch, "bingo"),
  );
};

export const useBingoSettingsStore = create(
  persist(
    (set) => ({
      settings: defaultBingoSettings,

      setSpecialEffectsEnabled: (v) => {
        const val = !!v;
        set((s) => ({
          settings: { ...s.settings, specialEffectsEnabled: val },
        }));
        broadcast({ specialEffectsEnabled: val });
      },

      setNicknamesEnabled: (v) => {
        const val = !!v;
        set((s) => ({
          settings: { ...s.settings, nicknamesEnabled: val },
        }));
        broadcast({ nicknamesEnabled: val });
      },

      /**
       * Enable/disable a specific Phase-2 special module by its key
       * (matches the entry in phase2Specials registry).
       */
      setSpecialEnabled: (key, v) => {
        const val = !!v;
        set((s) => ({
          settings: {
            ...s.settings,
            specials: { ...s.settings.specials, [key]: val },
          },
        }));
        broadcast({ specials: { [key]: val } });
      },

      /**
       * Patch one or more timing fields. Values are clamped to their
       * declared bounds so a mis-set field can never break the show.
       */
      setRevealTiming: (patch) => {
        const clamped = {};
        Object.entries(patch || {}).forEach(([k, v]) => {
          const b = TIMING_BOUNDS[k];
          if (!b) return;
          const n = Number(v);
          if (!Number.isFinite(n)) return;
          clamped[k] = Math.min(b.max, Math.max(b.min, Math.round(n)));
        });
        if (Object.keys(clamped).length === 0) return;
        set((s) => ({
          settings: {
            ...s.settings,
            revealTiming: { ...s.settings.revealTiming, ...clamped },
          },
        }));
        broadcast({ revealTiming: clamped });
      },

      resetRevealTiming: () => {
        set((s) => ({
          settings: { ...s.settings, revealTiming: { ...DEFAULT_TIMING } },
        }));
        broadcast({ revealTiming: { ...DEFAULT_TIMING } });
      },

      /**
       * Silent receiver — used by the Projector when it picks up a
       * SETTINGS_UPDATED broadcast from the Host. Never re-publishes.
       */
      applyRemotePatch: (patch) => {
        set((s) => {
          const nextSettings = { ...s.settings };
          if ("specialEffectsEnabled" in patch)
            nextSettings.specialEffectsEnabled = !!patch.specialEffectsEnabled;
          if ("nicknamesEnabled" in patch)
            nextSettings.nicknamesEnabled = !!patch.nicknamesEnabled;
          if (patch.revealTiming && typeof patch.revealTiming === "object") {
            nextSettings.revealTiming = {
              ...nextSettings.revealTiming,
              ...patch.revealTiming,
            };
          }
          if (patch.specials && typeof patch.specials === "object") {
            nextSettings.specials = {
              ...nextSettings.specials,
              ...patch.specials,
            };
          }
          return { settings: nextSettings };
        });
      },
    }),
    {
      name: "ic-bingo-plugin-settings",
      storage: createJSONStorage(() => localStorage),
      version: 5,
      migrate: (persisted, version) => {
        if (!persisted) return persisted;
        if (version < 2 && persisted.settings && !persisted.settings.revealTiming) {
          persisted.settings.revealTiming = { ...DEFAULT_TIMING };
        }
        if (version < 3 && persisted.settings && !persisted.settings.specials) {
          persisted.settings.specials = { doubles: true, sixSeven: true };
        }
        // v3 → v4: ensure Six Seven default exists for early v3 installs.
        if (
          version < 4 &&
          persisted.settings?.specials &&
          persisted.settings.specials.sixSeven === undefined
        ) {
          persisted.settings.specials.sixSeven = true;
        }
        // v4 → v5: seed Shy Ball (69) toggle for existing installs.
        if (
          version < 5 &&
          persisted.settings?.specials &&
          persisted.settings.specials.shyBall === undefined
        ) {
          persisted.settings.specials.shyBall = true;
        }
        return persisted;
      },
    },
  ),
);
